/**
 * Image loading animation
 * Animates [data-image-loading-animation] images inside [data-image-animation] sections.
 * Style, speed, and easing are controlled via theme settings (window.theme.settings.imageAnimation).
 */
(function () {
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  class ImageLoadingAnimation {
    constructor() {
      this.config =
        (window.theme && window.theme.settings && window.theme.settings.imageAnimation) ||
        {};

      if (this.config.enabled === false || prefersReducedMotion.matches) return;

      this.observer = null;
      this.scan();

      document.addEventListener('shopify:section:load', () => this.scan());
      document.addEventListener('theme:image-animation:scan', () => this.scan());
      document.addEventListener('collection:products-appended', () => this.scan());
    }

    scan() {
      document.querySelectorAll('[data-image-animation]').forEach((section) => {
        const images = section.querySelectorAll('[data-image-loading-animation]');
        let index = 0;

        images.forEach((node) => {
          const img = node.tagName === 'IMG' ? node : node.querySelector('img');

          if (!img || img.classList.contains('is-loaded') || img.dataset.imageAnimationBound) {
            return;
          }

          img.dataset.imageAnimationBound = 'true';
          this.bindImage(img, index);
          index += 1;
        });
      });
    }

    bindImage(img, index) {
      const reveal = () => this.observe(img, index);

      if (img.complete && img.naturalWidth > 0) {
        reveal();
      } else {
        img.addEventListener('load', reveal, { once: true });
        img.addEventListener(
          'error',
          () => {
            img.classList.add('is-loaded');
          },
          { once: true }
        );
      }
    }

    observe(img, index) {
      if (!this.observer) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.reveal(entry.target);
            });
          },
          { rootMargin: '50px 0px', threshold: 0.01 }
        );
      }

      const stagger = Number(this.config.stagger) || 0;
      img.dataset.animationDelay = String(index * stagger);

      if (this.isInViewport(img)) {
        this.reveal(img);
        return;
      }

      this.observer.observe(img);
    }

    isInViewport(el) {
      const rect = el.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }

    reveal(el) {
      if (el.classList.contains('is-loaded')) return;

      const delay = Number(el.dataset.animationDelay) || 0;

      window.setTimeout(() => {
        el.classList.add('is-loaded');
      }, delay);

      if (this.observer) {
        this.observer.unobserve(el);
      }
    }
  }

  function initImageLoadingAnimation() {
    window.theme = window.theme || {};
    window.theme.reinitImageAnimation = () => {
      document.dispatchEvent(new CustomEvent('theme:image-animation:scan'));
    };
    new ImageLoadingAnimation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initImageLoadingAnimation);
  } else {
    initImageLoadingAnimation();
  }
})();
